import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import React, { useCallback, useMemo } from "react";
import { Button } from "reactstrap";
import { useIntl } from "react-intl";
import yourLogoHere from "../../../assets/yourLogoHere.png";
import { CompanyPayload } from "../../../cores/company/interfaces/api/get-companies";
import { useGlobalCore } from "../../../cores/globals";
import { useMeCore } from "../../../cores/me";
import { useRouterCore } from "../../../cores/router";
import { Company } from "../../../cores/me/interfaces/api/get-meCompany";
import { PlanBadge } from "./PlanBadge";
import { ResellerBadge } from "./ResellerBadge";
import { RoleBadges } from "./RoleBadge";
import "../../styles/CompaniesTable.scss";

interface Props {
  companies: CompanyPayload[];
  onEditCompany: (companyId: string) => void;
  onDeleteCompany: (company: Company) => void;
  selectedIds: string[];
  onToggleCompany: (companyId: string) => void;
  onToggleAllCompanies: () => void;
  sortBy: CompanySortField;
  sortDirection: CompanySortDirection;
  onSort: (field: CompanySortField) => void;
}

type CompanySortField =
  | "name"
  | "userCount"
  | "plan"
  | "reseller"
  | "owner"
  | "location"
  | "createdAt";
type CompanySortDirection = "ASC" | "DESC";

export default function CompanyTable(props: Props) {
  const {
    companies,
    onEditCompany,
    onDeleteCompany,
    selectedIds,
    onToggleCompany,
    onToggleAllCompanies,
    sortBy,
    sortDirection,
    onSort,
  } = props;
  const intl = useIntl();

  const { reset } = useGlobalCore();
  const { selectCompany, company: meCompany, user, isAdmin, isReseller } = useMeCore();
  const { goToSocial } = useRouterCore();

  const isAllowedToEdit = useCallback(
    (role: any) =>
      Boolean(
        role?.owner ||
          role?.admin ||
          user?.userRole?.admin ||
          user?.userRole?.reseller
      ),
    [user]
  );

  const handleSelectCompany = useCallback(
    (company: Company, role?: any) => {
      if (role || isAdmin || isReseller) {
        reset();
        selectCompany(company.id);
        goToSocial();
      }
    },
    [goToSocial, isAdmin, isReseller, reset, selectCompany]
  );

  const selectableCompanyIds = useMemo(
    () =>
      companies
        .filter(({ company }) => Boolean(user?.userRole?.admin) && meCompany?.id !== company.id)
        .map(({ company }) => company.id),
    [companies, meCompany?.id, user?.userRole?.admin]
  );

  const allSelected =
    selectableCompanyIds.length > 0 &&
    selectableCompanyIds.every((companyId) => selectedIds.includes(companyId));

  const sortableHeader = (
    label: string,
    field: CompanySortField,
    className = ""
  ) => (
    <th
      className={cn("companiesTable__th", className)}
      aria-sort={
        sortBy === field
          ? sortDirection === "ASC"
            ? "ascending"
            : "descending"
          : "none"
      }
    >
      <button
        type="button"
        className="companiesTable__sortButton"
        onClick={() => onSort(field)}
      >
        <span>{label}</span>
        <span className="companiesTable__sortIndicator" aria-hidden="true">
          {sortBy === field ? (sortDirection === "ASC" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </th>
  );

  const formatDate = (value?: string) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? "-"
      : new Intl.DateTimeFormat(undefined, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }).format(date);
  };

  return (
    <div className="companiesTableWrapper">
      <div className="companiesTable__mobileToolbar">
        {Boolean(user?.userRole?.admin) && (
          <label className="companiesTable__mobileSelectAll">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={onToggleAllCompanies}
            />
            <span>
              {intl.formatMessage({
                id: "app.companies.bulk.selectAll",
                defaultMessage: "Seleccionar todas las empresas visibles",
              })}
            </span>
          </label>
        )}
        <div className="companiesTable__mobileSort">
          <label htmlFor="companies-mobile-sort">
            {intl.formatMessage({ id: "app.users.sortBy", defaultMessage: "Ordenar por" })}
          </label>
          <div>
            <select
              id="companies-mobile-sort"
              value={sortBy}
              onChange={(event) => onSort(event.target.value as CompanySortField)}
            >
              <option value="name">
                {intl.formatMessage({ id: "app.company.title", defaultMessage: "Empresa" })}
              </option>
              <option value="userCount">
                {intl.formatMessage({ id: "app.adminUsers.title", defaultMessage: "Usuarios" })}
              </option>
              <option value="plan">Plan</option>
              <option value="reseller">Reseller</option>
              <option value="owner">Owner</option>
              <option value="location">
                {intl.formatMessage({ id: "app.companies.location", defaultMessage: "Ubicación" })}
              </option>
              <option value="createdAt">
                {intl.formatMessage({ id: "app.users.createdAt", defaultMessage: "Cuenta creada" })}
              </option>
            </select>
            <button
              type="button"
              onClick={() => onSort(sortBy)}
              aria-label={intl.formatMessage({
                id: "app.users.changeSortDirection",
                defaultMessage: "Cambiar dirección",
              })}
            >
              {sortDirection === "ASC" ? "▲" : "▼"}
            </button>
          </div>
        </div>
      </div>
      <table className="companiesTable">
        <thead>
          <tr>
            <th className="companiesTable__th companiesTable__th--checkbox">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onToggleAllCompanies}
                aria-label={intl.formatMessage({
                  id: "app.companies.bulk.selectAll",
                  defaultMessage: "Seleccionar todas las empresas visibles",
                })}
              />
            </th>
            {sortableHeader(
              intl.formatMessage({ id: "app.company.title", defaultMessage: "Empresa" }),
              "name",
              "companiesTable__th--name"
            )}
            {sortableHeader(
              intl.formatMessage({ id: "app.adminUsers.title", defaultMessage: "Usuarios" }),
              "userCount"
            )}
            {sortableHeader("Plan", "plan")}
            {sortableHeader("Reseller", "reseller")}
            {sortableHeader("Owner", "owner")}
            {sortableHeader(
              intl.formatMessage({
                id: "app.companies.location",
                defaultMessage: "Ubicación",
              }),
              "location"
            )}
            {sortableHeader(
              intl.formatMessage({
                id: "app.users.createdAt",
                defaultMessage: "Cuenta creada",
              }),
              "createdAt"
            )}
            <th className="companiesTable__th companiesTable__th--actions">
              {intl.formatMessage({ id: "app.table.actions", defaultMessage: "Acciones" })}
            </th>
          </tr>
        </thead>
        <tbody>
          {companies.map(({ company, role, plan, owner, userCount }) => {
            const isSelected = meCompany?.id === company.id;
            const canEdit = isAllowedToEdit(role);
            const canDelete = user?.userRole?.admin && !isSelected;
            const canSelect = !isSelected && Boolean(role || isAdmin || isReseller);

            const companyLogo = !company?.avatarUrl ? yourLogoHere : company.avatarUrl;

            return (
              <tr
                key={company.id}
                className={cn("companiesTable__tr", { "companiesTable__tr--selected": isSelected })}
                data-selected={selectedIds.includes(company.id) ? "true" : "false"}
              >
                <td
                  className="companiesTable__td companiesTable__td--checkbox"
                  data-label={intl.formatMessage({
                    id: "app.users.bulk.selectOneShort",
                    defaultMessage: "Seleccionar",
                  })}
                >
                  {canDelete ? (
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(company.id)}
                      onChange={() => onToggleCompany(company.id)}
                      aria-label={intl.formatMessage(
                        {
                          id: "app.companies.bulk.selectOne",
                          defaultMessage: "Seleccionar empresa {name}",
                        },
                        { name: company.name }
                      )}
                    />
                  ) : null}
                </td>
                <td
                  className="companiesTable__td companiesTable__td--name"
                  data-label={intl.formatMessage({
                    id: "app.company.title",
                    defaultMessage: "Empresa",
                  })}
                >
                  <div className="companiesTable__nameCell">
                    <img
                      src={companyLogo}
                      alt={company.name}
                      className="companiesTable__avatar"
                    />
                    <div className="companiesTable__nameMeta">
                      <div className="companiesTable__companyName">{company.name}</div>
                      {role ? (
                        <div className="companiesTable__roleBadges">
                          <RoleBadges roles={role} id={company.id} />
                        </div>
                      ) : null}
                    </div>
                  </div>
                </td>

                <td
                  className="companiesTable__td companiesTable__td--count"
                  data-label={intl.formatMessage({
                    id: "app.adminUsers.title",
                    defaultMessage: "Usuarios",
                  })}
                >
                  <span className="companiesTable__count">{userCount || 0}</span>
                </td>

                <td className="companiesTable__td" data-label="Plan">
                  {plan ? (
                    <PlanBadge plan={plan} show />
                  ) : (
                    <FontAwesomeIcon
                      className="text-danger"
                      size="lg"
                      icon={["fas", "exclamation-triangle"]}
                    />
                  )}
                </td>

                <td className="companiesTable__td" data-label="Reseller">
                  {company.reseller ? <ResellerBadge reseller={company.reseller} show /> : "-"}
                </td>

                <td className="companiesTable__td" data-label="Owner">
                  {owner ? `${owner.lastName} ${owner.firstName}` : "-"}
                </td>

                <td
                  className="companiesTable__td"
                  data-label={intl.formatMessage({
                    id: "app.companies.location",
                    defaultMessage: "Ubicación",
                  })}
                >
                  {[company.city, company.country].filter(Boolean).join(", ") || "-"}
                </td>

                <td
                  className="companiesTable__td"
                  data-label={intl.formatMessage({
                    id: "app.users.createdAt",
                    defaultMessage: "Cuenta creada",
                  })}
                >
                  {formatDate(company.createdAt)}
                </td>

                <td
                  className="companiesTable__td companiesTable__td--actions"
                  data-label={intl.formatMessage({
                    id: "app.table.actions",
                    defaultMessage: "Acciones",
                  })}
                >
                  <div className="companiesTable__actions">
                    {canSelect && (
                      <Button
                        color="primary"
                        size="sm"
                        className="companiesTable__btn"
                        onClick={() => handleSelectCompany(company, role)}
                      >
                        {intl.formatMessage({ id: "app.select.placeholder", defaultMessage: "Seleccionar" })}
                      </Button>
                    )}

                    {canEdit && (
                      <Button
                        color="secondary"
                        size="sm"
                        className="companiesTable__btn"
                        onClick={() => onEditCompany(company.id)}
                      >
                        {intl.formatMessage({ id: "app.adminCompanies.edit", defaultMessage: "Editar" })}
                      </Button>
                    )}

                    {canDelete && (
                      <Button
                        color="danger"
                        size="sm"
                        outline
                        className="companiesTable__btn companiesTable__btn--danger"
                        onClick={() => onDeleteCompany(company)}
                      >
                        <FontAwesomeIcon
                          icon={["fas", "trash"]}
                          className="mr-2"
                          size="1x"
                        />
                        {intl.formatMessage({ id: "app.adminCompanies.delete", defaultMessage: "Eliminar" })}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

